import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Stethoscope,
  FileText,
  ArrowLeft,
  Loader2,
  Shield,
  CheckCircle,
  AlertCircle,
  Video,
  Sparkles,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";
import { formatApiError } from "../lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ScriptGeneratorPage() {
  const { doctorId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [doctor, setDoctor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checkingCompliance, setCheckingCompliance] = useState(false);
  const [topic, setTopic] = useState(location.state?.topic || "");
  const [format, setFormat] = useState(location.state?.format || "Explainer");
  const [script, setScript] = useState(null);
  const [compliance, setCompliance] = useState(null);
  const [activeTab, setActiveTab] = useState("generate");
  const [manualContent, setManualContent] = useState("");
  const [savingManual, setSavingManual] = useState(false);

  useEffect(() => {
    fetchDoctor();
  }, [doctorId]);

  const fetchDoctor = async () => {
    try {
      const response = await axios.get(`${API}/doctors/${doctorId}`);
      setDoctor(response.data);
    } catch (error) {
      console.error("Error fetching doctor:", error);
      toast.error("Failed to load profile");
    }
  };

  const generateScript = async () => {
    if (!topic.trim()) {
      toast.error("Please enter a topic");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/content/script`, {
        doctor_id: doctorId,
        topic,
        format,
        language: doctor?.language_preference || "English",
        country: doctor?.country || "India",
      });

      setScript(response.data);
      setCompliance(null);
      toast.success("Script generated successfully!");
      setActiveTab("script");
    } catch (error) {
      console.error("Error generating script:", error);
      toast.error(
        formatApiError(
          error.response?.data?.detail,
          "Failed to generate script",
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  const saveManualScript = async () => {
    if (!manualContent.trim()) {
      toast.error("Please write your script first");
      return;
    }
    setSavingManual(true);
    try {
      const response = await axios.post(`${API}/content/script/manual`, {
        doctor_id: doctorId,
        topic: topic || "My script",
        content: manualContent,
        language: doctor?.language_preference || "English",
        country: doctor?.country || "India",
      });
      setScript(response.data);
      setCompliance(null);
      toast.success("Your script was saved!");
      setActiveTab("script");
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail, "Failed to save script"));
    } finally {
      setSavingManual(false);
    }
  };

  const checkCompliance = async () => {
    if (!script) return;

    setCheckingCompliance(true);
    try {
      const response = await axios.post(`${API}/content/compliance`, {
        script_id: script.id,
        country: doctor?.country || "India",
        script_content: {
          hook: script.hook,
          explanation: script.explanation,
          cta: script.cta,
          disclaimer: script.disclaimer,
        },
      });

      setCompliance(response.data);
      if (response.data.saved) {
        toast.success("Compliance report saved! View in Compliance History.");
      } else {
        toast.success("Compliance check completed!");
      }
      setActiveTab("compliance");
    } catch (error) {
      console.error("Error checking compliance:", error);
      toast.error(
        formatApiError(
          error.response?.data?.detail,
          "Failed to check compliance",
        ),
      );
    } finally {
      setCheckingCompliance(false);
    }
  };

  const getRiskLevelColor = (level) => {
    switch (level?.toLowerCase()) {
      case "low":
        return "text-green-600 bg-green-50";
      case "medium":
        return "text-yellow-600 bg-yellow-50";
      case "high":
        return "text-red-600 bg-red-50";
      default:
        return "text-gray-600 bg-gray-50";
    }
  };

  const getRiskIcon = (level) => {
    switch (level?.toLowerCase()) {
      case "low":
        return <CheckCircle className="w-5 h-5" strokeWidth={1.5} />;
      case "medium":
        return <AlertCircle className="w-5 h-5" strokeWidth={1.5} />;
      case "high":
        return <AlertCircle className="w-5 h-5" strokeWidth={1.5} />;
      default:
        return <Shield className="w-5 h-5" strokeWidth={1.5} />;
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] grain-texture">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate(`/dashboard/${doctorId}`)}
              variant="ghost"
              size="sm"
              className="rounded-full"
              data-testid="script-back-btn"
            >
              <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
            </Button>
            <Stethoscope className="w-8 h-8 text-[#2C5F44]" strokeWidth={1.5} />
            <span
              className="text-2xl font-bold text-[#2C5F44]"
              style={{ fontFamily: "Nunito, sans-serif" }}
            >
              Script Generator
            </span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-6 py-12">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 mb-8 bg-white rounded-2xl p-1 border border-[#2C5F44]/10">
            <TabsTrigger
              value="generate"
              className="rounded-xl data-[state=active]:bg-[#2C5F44] data-[state=active]:text-white"
              data-testid="script-tab-generate"
            >
              <FileText className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Generate
            </TabsTrigger>
            <TabsTrigger
              value="script"
              disabled={!script}
              className="rounded-xl data-[state=active]:bg-[#2C5F44] data-[state=active]:text-white"
              data-testid="script-tab-script"
            >
              Script
            </TabsTrigger>
            <TabsTrigger
              value="compliance"
              disabled={!compliance}
              className="rounded-xl data-[state=active]:bg-[#2C5F44] data-[state=active]:text-white"
              data-testid="script-tab-compliance"
            >
              <Shield className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Compliance
            </TabsTrigger>
          </TabsList>

          {/* Generate Tab */}
          <TabsContent value="generate">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-3xl p-8 border border-[#2C5F44]/10"
            >
              <h2
                className="text-2xl font-bold text-[#1C2A21] mb-6"
                style={{ fontFamily: "Nunito, sans-serif" }}
              >
                Create Your Script
              </h2>

              <div className="space-y-6">
                <div>
                  <Label
                    htmlFor="topic"
                    className="text-[#1C2A21] font-medium mb-2 block"
                  >
                    Video Topic *
                  </Label>
                  <Input
                    id="topic"
                    data-testid="script-topic-input"
                    placeholder="e.g., Top 5 Heart-Healthy Foods"
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                    className="rounded-xl border-[#2C5F44]/20 focus:border-[#82A08B] focus:ring-[#82A08B]"
                  />
                </div>

                <div>
                  <Label
                    htmlFor="format"
                    className="text-[#1C2A21] font-medium mb-2 block"
                  >
                    Format
                  </Label>
                  <Select value={format} onValueChange={setFormat}>
                    <SelectTrigger
                      data-testid="script-format-select"
                      className="rounded-xl border-[#2C5F44]/20 focus:border-[#82A08B] focus:ring-[#82A08B]"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Reel">Reel</SelectItem>
                      <SelectItem value="FAQ">FAQ</SelectItem>
                      <SelectItem value="Myth vs Fact">Myth vs Fact</SelectItem>
                      <SelectItem value="Explainer">Explainer</SelectItem>
                      <SelectItem value="Quick Tip">Quick Tip</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="pt-4">
                  <Button
                    onClick={generateScript}
                    disabled={loading}
                    className="button-pill bg-[#2C5F44] hover:bg-[#244b36] text-white rounded-full w-full py-6"
                    data-testid="script-generate-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2
                          className="w-5 h-5 mr-2 animate-spin"
                          strokeWidth={1.5}
                        />
                        Generating Script...
                      </>
                    ) : (
                      <>
                        <FileText className="w-5 h-5 mr-2" strokeWidth={1.5} />
                        Generate Script
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }} className="bg-white rounded-3xl p-8 border border-[#2C5F44]/10 mt-6">
              <h2 className="text-2xl font-bold text-[#1C2A21] mb-2" style={{ fontFamily: "Nunito, sans-serif" }}>Or write your own script</h2>
              <p className="text-sm text-[#5C7063] mb-6">Prefer to write it yourself? Paste or type your script and we will save it to your library.</p>
              <Label htmlFor="manual-content" className="text-[#1C2A21] font-medium mb-2 block">Your Script</Label>
              <textarea id="manual-content" data-testid="script-manual-input" placeholder="Write or paste your own script here..." value={manualContent} onChange={(e) => setManualContent(e.target.value)} rows={7} className="w-full rounded-xl border border-[#2C5F44]/20 p-3 text-[#1C2A21] bg-white focus:outline-none focus:border-[#82A08B]" />
              <Button onClick={saveManualScript} disabled={savingManual} className="button-pill bg-[#2C5F44] hover:bg-[#244b36] text-white rounded-full w-full py-6 mt-4" data-testid="script-manual-save-btn">
                {savingManual ? (<><Loader2 className="w-5 h-5 mr-2 animate-spin" strokeWidth={1.5} />Saving...</>) : (<><FileText className="w-5 h-5 mr-2" strokeWidth={1.5} />Save My Script</>)}
              </Button>
            </motion.div>
            </TabsContent>

          {/* Script Tab */}
          <TabsContent value="script">
            {script && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="space-y-6"
              >
                <div className="bg-white rounded-3xl p-8 border border-[#2C5F44]/10">
                  <h2
                    className="text-2xl font-bold text-[#1C2A21] mb-6"
                    style={{ fontFamily: "Nunito, sans-serif" }}
                  >
                    {script.topic}
                  </h2>

                  <div className="space-y-6">
                    <div>
                      <h3 className="text-sm font-semibold text-[#2C5F44] mb-2 uppercase tracking-wide">
                        Hook (First 3 seconds)
                      </h3>
                      <p className="text-[#1C2A21] bg-[#F4F1EA] p-4 rounded-xl">
                        {script.hook}
                      </p>
                    </div>

                    <div>
                      <h3 className="text-sm font-semibold text-[#2C5F44] mb-2 uppercase tracking-wide">
                        Explanation
                      </h3>
                      <p className="text-[#1C2A21] bg-[#F4F1EA] p-4 rounded-xl whitespace-pre-wrap">
                        {script.explanation}
                      </p>
                    </div>

                    <div>
                      <h3 className="text-sm font-semibold text-[#2C5F44] mb-2 uppercase tracking-wide">
                        Call to Action
                      </h3>
                      <p className="text-[#1C2A21] bg-[#F4F1EA] p-4 rounded-xl">
                        {script.cta}
                      </p>
                    </div>

                    <div>
                      <h3 className="text-sm font-semibold text-[#2C5F44] mb-2 uppercase tracking-wide">
                        Disclaimer
                      </h3>
                      <p className="text-[#5C7063] bg-[#F4F1EA] p-4 rounded-xl text-sm italic">
                        {script.disclaimer}
                      </p>
                    </div>
                  </div>

                  <div className="mt-8 pt-6 border-t border-[#2C5F44]/10">
                    {/* Primary choice: AI Avatar (recommended) vs Record */}
                    <h3
                      className="text-lg font-bold text-[#1C2A21] mb-1"
                      style={{ fontFamily: "Nunito, sans-serif" }}
                    >
                      Turn this script into a video
                    </h3>
                    <p className="text-sm text-[#5C7063] mb-4">
                      Choose how you want to deliver it.
                    </p>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <button
                        type="button"
                        onClick={() =>
                          navigate(`/video/${doctorId}`, { state: { script } })
                        }
                        className="text-left bg-gradient-to-br from-[#2C5F44] to-[#1f4631] rounded-2xl p-5 text-white relative overflow-hidden group hover:shadow-xl hover:shadow-[#2C5F44]/30 transition"
                        data-testid="script-choice-ai-avatar"
                      >
                        <div className="absolute top-3 right-3 px-2 py-0.5 bg-[#D98A6C] text-white text-[10px] font-bold rounded-full uppercase">
                          Recommended
                        </div>
                        <Sparkles className="w-7 h-7 mb-3" strokeWidth={1.5} />
                        <div
                          className="font-bold mb-1"
                          style={{ fontFamily: "Nunito, sans-serif" }}
                        >
                          Use AI Avatar
                        </div>
                        <p className="text-sm text-white/80">
                          No camera. Your face & voice generate the video —
                          captions, B-roll, branding included.
                        </p>
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          navigate(`/video/${doctorId}`, {
                            state: { script, mode: "record" },
                          })
                        }
                        className="text-left bg-white border border-[#2C5F44]/20 rounded-2xl p-5 hover:border-[#2C5F44] hover:shadow-md transition"
                        data-testid="script-choice-record"
                      >
                        <Video
                          className="w-7 h-7 mb-3 text-[#2C5F44]"
                          strokeWidth={1.5}
                        />
                        <div
                          className="font-bold text-[#1C2A21] mb-1"
                          style={{ fontFamily: "Nunito, sans-serif" }}
                        >
                          Record Yourself
                        </div>
                        <p className="text-sm text-[#5C7063]">
                          Prefer to film? Upload your recorded clip and we'll
                          polish it with captions and branding.
                        </p>
                      </button>
                    </div>

                    <div className="mt-6">
                      <Button
                        onClick={checkCompliance}
                        disabled={checkingCompliance}
                        variant="outline"
                        className="rounded-full border-[#2C5F44]/20 text-[#2C5F44] hover:bg-[#2C5F44]/5"
                        data-testid="script-check-compliance-btn"
                      >
                        {checkingCompliance ? (
                          <>
                            <Loader2
                              className="w-4 h-4 mr-2 animate-spin"
                              strokeWidth={1.5}
                            />
                            Checking…
                          </>
                        ) : (
                          <>
                            <Shield
                              className="w-4 h-4 mr-2"
                              strokeWidth={1.5}
                            />
                            Run advanced compliance audit
                          </>
                        )}
                      </Button>
                      <span className="text-xs text-[#5C7063] ml-3">
                        Already auto-fixed during generation. Run again only if
                        you've edited the script.
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </TabsContent>

          {/* Compliance Tab */}
          <TabsContent value="compliance">
            {compliance && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="space-y-6"
              >
                <div className="bg-white rounded-3xl p-8 border border-[#2C5F44]/10">
                  <h2
                    className="text-2xl font-bold text-[#1C2A21] mb-6"
                    style={{ fontFamily: "Nunito, sans-serif" }}
                  >
                    Compliance Report
                  </h2>

                  {/* Risk Level */}
                  <div className="mb-8">
                    <div
                      className={`inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold ${getRiskLevelColor(compliance.risk_level)}`}
                    >
                      {getRiskIcon(compliance.risk_level)}
                      Risk Level: {compliance.risk_level}
                    </div>
                  </div>

                  {/* Violations */}
                  {compliance.violations &&
                    compliance.violations.length > 0 && (
                      <div className="mb-6">
                        <h3
                          className="text-lg font-semibold text-[#1C2A21] mb-3"
                          style={{ fontFamily: "Nunito, sans-serif" }}
                        >
                          Violations Found
                        </h3>
                        <ul className="space-y-2">
                          {compliance.violations.map((violation, index) => (
                            <li
                              key={index}
                              className="flex items-start gap-2 text-red-600 bg-red-50 p-3 rounded-xl"
                            >
                              <AlertCircle
                                className="w-5 h-5 mt-0.5 flex-shrink-0"
                                strokeWidth={1.5}
                              />
                              <span>{violation}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                  {/* Missing Elements */}
                  {compliance.missing_elements &&
                    compliance.missing_elements.length > 0 && (
                      <div className="mb-6">
                        <h3
                          className="text-lg font-semibold text-[#1C2A21] mb-3"
                          style={{ fontFamily: "Nunito, sans-serif" }}
                        >
                          Missing Elements
                        </h3>
                        <ul className="space-y-2">
                          {compliance.missing_elements.map((element, index) => (
                            <li
                              key={index}
                              className="flex items-start gap-2 text-yellow-600 bg-yellow-50 p-3 rounded-xl"
                            >
                              <AlertCircle
                                className="w-5 h-5 mt-0.5 flex-shrink-0"
                                strokeWidth={1.5}
                              />
                              <span>{element}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                  {/* Suggestions */}
                  {compliance.suggestions &&
                    compliance.suggestions.length > 0 && (
                      <div className="mb-6">
                        <h3
                          className="text-lg font-semibold text-[#1C2A21] mb-3"
                          style={{ fontFamily: "Nunito, sans-serif" }}
                        >
                          Suggestions
                        </h3>
                        <ul className="space-y-2">
                          {compliance.suggestions.map((suggestion, index) => (
                            <li
                              key={index}
                              className="flex items-start gap-2 text-[#2C5F44] bg-[#F4F1EA] p-3 rounded-xl"
                            >
                              <CheckCircle
                                className="w-5 h-5 mt-0.5 flex-shrink-0"
                                strokeWidth={1.5}
                              />
                              <span>{suggestion}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                  {/* Approved Version */}
                  {compliance.approved_version && (
                    <div className="mt-8 pt-6 border-t border-[#2C5F44]/10">
                      <h3
                        className="text-lg font-semibold text-[#1C2A21] mb-4"
                        style={{ fontFamily: "Nunito, sans-serif" }}
                      >
                        Approved Version
                      </h3>
                      <div className="space-y-4">
                        {compliance.approved_version.hook && (
                          <div>
                            <p className="text-sm font-semibold text-[#2C5F44] mb-1">
                              Hook:
                            </p>
                            <p className="text-[#1C2A21] bg-green-50 p-3 rounded-xl">
                              {compliance.approved_version.hook}
                            </p>
                          </div>
                        )}
                        {compliance.approved_version.explanation && (
                          <div>
                            <p className="text-sm font-semibold text-[#2C5F44] mb-1">
                              Explanation:
                            </p>
                            <p className="text-[#1C2A21] bg-green-50 p-3 rounded-xl whitespace-pre-wrap">
                              {compliance.approved_version.explanation}
                            </p>
                          </div>
                        )}
                        {compliance.approved_version.cta && (
                          <div>
                            <p className="text-sm font-semibold text-[#2C5F44] mb-1">
                              CTA:
                            </p>
                            <p className="text-[#1C2A21] bg-green-50 p-3 rounded-xl">
                              {compliance.approved_version.cta}
                            </p>
                          </div>
                        )}
                        {compliance.approved_version.disclaimer && (
                          <div>
                            <p className="text-sm font-semibold text-[#2C5F44] mb-1">
                              Disclaimer:
                            </p>
                            <p className="text-[#5C7063] bg-green-50 p-3 rounded-xl text-sm italic">
                              {compliance.approved_version.disclaimer}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
